import { create } from "zustand";
import { persist } from "zustand/middleware";

/** Default + bounds for the docked meeting panel width (px). */
export const MEETING_DOCK_DEFAULT_WIDTH = 400;

interface MeetingDockState {
  // The single meeting the logged-in user currently holds open. The live WebRTC
  // connection is owned by the global provider (mounted in ProtectedLayout), so
  // it survives navigation: "full" renders it on the room route, "docked" renders
  // it as a resizable right-hand panel next to whatever page is open.
  occurrenceId: string | null;
  mode: "full" | "docked";
  dockWidth: number;
  /** Take the meeting full-screen (called by the room route on mount). */
  enterFull: (id: string) => void;
  /** Collapse the active meeting into the side panel (no-op if none). */
  dock: () => void;
  /** Expand the docked meeting back to full-screen (no-op if none). */
  expand: () => void;
  /** Leave the meeting entirely — the provider tears the connection down. */
  leave: () => void;
  setDockWidth: (w: number) => void;
}

export const useMeetingDockStore = create<MeetingDockState>()(
  persist(
    (set) => ({
      occurrenceId: null,
      mode: "full",
      dockWidth: MEETING_DOCK_DEFAULT_WIDTH,
      enterFull: (id) => set({ occurrenceId: id, mode: "full" }),
      dock: () => set((s) => (s.occurrenceId ? { mode: "docked" } : {})),
      expand: () => set((s) => (s.occurrenceId ? { mode: "full" } : {})),
      leave: () => set({ occurrenceId: null, mode: "full" }),
      setDockWidth: (dockWidth) => set({ dockWidth }),
    }),
    {
      name: "elg5-meeting-dock",
      // Only the width is sticky. We deliberately do NOT persist occurrenceId:
      // reloading the page should not silently rejoin a call.
      partialize: (s) => ({ dockWidth: s.dockWidth }),
    },
  ),
);
